import { createModalFocusScope } from '../../components/modal-focus-scope.js';
import { canAnimateElement, prefersReducedMotion, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import { checkNameField, flashRowSaved } from './feedback-controller.js';
import { readErrorMessage } from '../../components/write-request.js';
import { submitDictionaryMutation } from './mutation-request.js';

const UNKNOWN_MESSAGE = 'Could not confirm the change was saved. Reload the page before trying again.';

const FRAGMENT_TYPE = 'status';

/**
 * Status list behaviour on the Library settings page.
 *
 * Every write posts the same form the server already understands and swaps in the section the server
 * renders back, so counts, the default badge, move-button limits and delete availability are never
 * recomputed here. The one thing a swap cannot know is what the owner had typed but not saved, so
 * unsaved names and colors are lifted out before the swap and put back after it - that is the whole
 * reason this file exists rather than letting the page reload.
 *
 * This module owns the status section: its dirty state, its palette toggles, its feedback block, its
 * delete modal, and the page's status announcer (a live region outside the section, so it survives
 * swaps). A palette's open state is not carried across a swap - the fresh render starts closed, so a
 * successful write first collapses the open palettes (and the saved row's unsaved line) in the old
 * section and only then swaps it, which keeps the replacement from jumping.
 * Inline `--status-rgb` stays owned by status-color-preview.js, which is why restoring a draft color
 * here dispatches `input` instead of writing the property.
 */
export function initStatusSection({
    root,
    windowRef = globalThis.window,
    fetchImpl = globalThis.fetch,
    dictionaryFeedback = null,
    parseSection = (html) => new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-status-section]'),
}) {
    let section = root.querySelector?.('[data-status-section]');
    if (!(section instanceof HTMLElement) || typeof fetchImpl !== 'function') {
        return null;
    }

    let pending = false;
    const announcer = root.querySelector?.('[data-status-announcer]');
    // palette -> { open, animation, settled }: the one running reveal or collapse of that palette.
    const paletteMotion = new WeakMap();

    const rowInputs = (row) => ({
        name: row.querySelector('[data-status-name-input]'),
        color: row.querySelector('[data-status-color-input]'),
    });

    const collectRowPositions = () => new Map(
        Array.from(section.querySelectorAll('[data-status-row]'))
            .map((row) => [row.dataset.statusId, row.getBoundingClientRect?.().top])
            .filter(([id, top]) => typeof id === 'string' && id !== '' && Number.isFinite(top)),
    );

    const collectCreateSnapshot = () => {
        const list = section.querySelector('[data-status-rows]');

        return {
            ids: new Set(Array.from(section.querySelectorAll('[data-status-row]'))
                .map((row) => row.dataset.statusId)),
            listHeight: list?.getBoundingClientRect?.().height,
        };
    };

    const animateCreatedRow = async (snapshot) => {
        if (snapshot === null) {
            return;
        }

        const list = section.querySelector('[data-status-rows]');
        const row = Array.from(section.querySelectorAll('[data-status-row]'))
            .find((candidate) => !snapshot.ids.has(candidate.dataset.statusId));
        const listHeight = list?.getBoundingClientRect?.().height;
        if (
            !(list instanceof HTMLElement)
            || !(row instanceof HTMLElement)
            || !Number.isFinite(snapshot.listHeight)
            || !Number.isFinite(listHeight)
            || listHeight <= snapshot.listHeight
            || !canAnimateElement(list, windowRef)
            || !canAnimateElement(row, windowRef)
        ) {
            return;
        }

        const options = {
            duration: readCssTimeMs(row, '--status-create-duration', 440),
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            fill: 'both',
        };
        const animations = [
            playAnimation(list, [
                { height: `${snapshot.listHeight}px`, overflow: 'hidden' },
                { height: `${listHeight}px`, overflow: 'hidden' },
            ], options),
            playAnimation(row, [
                { opacity: 0, transform: 'translateY(-12px) scale(0.985)' },
                { opacity: 1, transform: 'translateY(0) scale(1)' },
            ], options),
        ].filter((animation) => animation !== null);
        await settleAnimations(animations);
        cancelAnimations(animations);
    };

    const animateDeletedRow = async (row) => {
        const list = row?.parentElement;
        const height = row?.getBoundingClientRect?.().height;
        if (
            !(row instanceof HTMLElement)
            || !(list instanceof HTMLElement)
            || !Number.isFinite(height)
            || height <= 0
            || !canAnimateElement(row, windowRef)
        ) {
            return;
        }

        const rowStyles = typeof globalThis.getComputedStyle === 'function'
            ? globalThis.getComputedStyle(row)
            : null;
        const listStyles = typeof globalThis.getComputedStyle === 'function'
            ? globalThis.getComputedStyle(list)
            : null;
        const styleValue = (styles, property) => styles?.getPropertyValue?.(property).trim() || '0px';
        const gap = Number.parseFloat(styleValue(listStyles, 'row-gap')) || 0;
        const animation = playAnimation(row, [
            {
                height: `${height}px`,
                paddingTop: styleValue(rowStyles, 'padding-top'),
                paddingBottom: styleValue(rowStyles, 'padding-bottom'),
                borderTopWidth: styleValue(rowStyles, 'border-top-width'),
                borderBottomWidth: styleValue(rowStyles, 'border-bottom-width'),
                marginBottom: '0px',
                opacity: 1,
                overflow: 'hidden',
                transform: 'translateY(0) scale(1)',
            },
            {
                height: '0px',
                paddingTop: '0px',
                paddingBottom: '0px',
                borderTopWidth: '0px',
                borderBottomWidth: '0px',
                marginBottom: `${-gap}px`,
                opacity: 0,
                overflow: 'hidden',
                transform: 'translateY(-10px) scale(0.985)',
            },
        ], {
            duration: readCssTimeMs(row, '--status-delete-duration', 400),
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            fill: 'both',
        });
        if (animation === null) {
            return;
        }

        await settleAnimations([animation]);
        animation.cancel();
    };

    const animateReorderedRows = async (previousPositions, movedStatusId) => {
        if (!(previousPositions instanceof Map)) {
            return;
        }

        const animations = [];
        let liftedRow = null;
        section.querySelectorAll('[data-status-row]').forEach((row) => {
            const previousTop = previousPositions.get(row.dataset.statusId);
            const currentTop = row.getBoundingClientRect?.().top;
            const delta = previousTop - currentTop;
            if (!Number.isFinite(delta) || delta === 0 || !canAnimateElement(row, windowRef)) {
                return;
            }

            const moved = row.dataset.statusId === movedStatusId;
            if (moved) {
                row.setAttribute('data-status-reorder-active', '');
                liftedRow = row;
            }
            const animation = playAnimation(row, moved ? [
                { opacity: 1, transform: `translateY(${delta}px) scale(1)` },
                { opacity: 1, offset: 0.55, transform: `translateY(${delta * 0.45}px) scale(1.012)` },
                { opacity: 1, transform: 'translateY(0) scale(1)' },
            ] : [
                { opacity: 1, transform: `translateY(${delta}px)` },
                { opacity: 0.72, offset: 0.55, transform: `translateY(${delta * 0.45}px)` },
                { opacity: 1, transform: 'translateY(0)' },
            ], {
                duration: readCssTimeMs(row, '--status-reorder-duration', 400),
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
                fill: 'both',
            });
            if (animation !== null) {
                animations.push(animation);
            }
        });
        await settleAnimations(animations);
        cancelAnimations(animations);
        liftedRow?.removeAttribute('data-status-reorder-active');
    };

    /**
     * Reveals or collapses a row's palette (height, opacity and its top margin, so nothing below jumps
     * when it is shown or hidden). A press mid-animation reverses the running one from where it
     * is. A closed palette ends `hidden`, out of the Tab order.
     *
     * @returns {Promise<void>} settles when the palette has reached the requested state
     */
    const setPaletteOpen = (row, open) => {
        const toggle = row?.querySelector?.('[data-status-color-toggle]');
        const palette = row?.querySelector?.('[data-status-palette]');
        if (!(palette instanceof HTMLElement)) {
            return Promise.resolve();
        }
        if (toggle instanceof HTMLElement) {
            toggle.setAttribute('aria-expanded', String(open));
        }

        const running = paletteMotion.get(palette);
        if (running !== undefined) {
            if (running.open !== open) {
                running.open = open;
                running.animation.reverse();
            }

            return running.settled;
        }
        if (palette.hidden === !open) {
            return Promise.resolve();
        }

        palette.hidden = false;
        if (!canAnimateElement(palette, windowRef)) {
            palette.hidden = !open;

            return Promise.resolve();
        }

        const height = palette.getBoundingClientRect?.().height ?? 0;
        const styles = typeof globalThis.getComputedStyle === 'function'
            ? globalThis.getComputedStyle(palette)
            : null;
        const marginTop = styles?.getPropertyValue?.('margin-top') || '0px';
        const closed = { height: '0px', marginTop: '0px', opacity: 0, overflow: 'hidden' };
        const shown = { height: `${height}px`, marginTop, opacity: 1, overflow: 'hidden' };
        const animation = playAnimation(palette, open ? [closed, shown] : [shown, closed], {
            duration: readCssTimeMs(palette, '--status-palette-duration', 200),
            // `--easing`, the curve of the row's own CSS transitions.
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            fill: 'both',
        });
        if (animation === null) {
            palette.hidden = !open;

            return Promise.resolve();
        }

        const motion = { open, animation, settled: null };
        motion.settled = settleAnimations([animation]).then(() => {
            paletteMotion.delete(palette);
            palette.hidden = !motion.open;
            animation.cancel();
        });
        paletteMotion.set(palette, motion);

        return motion.settled;
    };

    /**
     * The fresh render arrives with every palette closed and the saved row clean, so the old section
     * gets there first, with the same motion the owner sees on a toggle or a revert.
     */
    const collapseBeforeSwap = async (savedRow) => {
        const collapses = Array.from(section.querySelectorAll('[data-status-row]'))
            .filter((row) => {
                const palette = row.querySelector('[data-status-palette]');

                return palette instanceof HTMLElement
                    && (palette.hidden === false || paletteMotion.get(palette)?.open === true);
            })
            .map((row) => setPaletteOpen(row, false));

        // The row's own dirty-to-clean CSS transition, the one `Revert` already plays.
        if (savedRow instanceof HTMLElement && savedRow.dataset.statusState === 'dirty') {
            savedRow.dataset.statusState = 'clean';
            // Same policy as every other motion here: none under reduced motion, so no wait either.
            if (canAnimateElement(savedRow, windowRef)) {
                const wait = readCssTimeMs(savedRow, '--duration-base', 200);
                const setTimer = typeof windowRef?.setTimeout === 'function'
                    ? windowRef.setTimeout.bind(windowRef)
                    : globalThis.setTimeout;
                collapses.push(new Promise((resolve) => {
                    setTimer(resolve, wait);
                }));
            }
        }

        await Promise.all(collapses);
    };

    const syncRow = (row) => {
        const { name, color } = rowInputs(row);
        if (!(name instanceof HTMLInputElement) || !(color instanceof HTMLInputElement)) {
            return;
        }

        const value = color.value.toLowerCase();
        let onPalette = false;
        row.querySelectorAll('[data-status-color-swatch]').forEach((swatch) => {
            const isMatch = swatch.value.toLowerCase() === value;
            swatch.checked = isMatch;
            onPalette = onPalette || isMatch;
        });
        const custom = row.querySelector('[data-status-color-custom]');
        if (custom instanceof HTMLElement) {
            custom.toggleAttribute('data-status-swatch-selected', !onPalette);
        }

        const dirty = name.value !== name.defaultValue || color.value !== color.defaultValue;
        const save = row.querySelector('[data-status-save]');
        const revert = row.querySelector('[data-status-revert]');
        const state = row.querySelector('[data-status-row-state]');
        row.dataset.statusState = dirty ? 'dirty' : 'clean';
        if (save instanceof HTMLElement) {
            save.disabled = !dirty;
        }
        if (revert instanceof HTMLElement) {
            revert.disabled = !dirty;
        }
        if (state instanceof HTMLElement) {
            state.hidden = !dirty;
        }
    };

    /**
     * @param {HTMLElement|null} skipRow the row being saved - its input is about to become the stored
     *     value, so carrying it over would leave a saved row looking unsaved
     */
    const collectDrafts = (skipRow) => {
        const drafts = new Map();
        section.querySelectorAll('[data-status-row]').forEach((row) => {
            const id = row.dataset.statusId;
            const { name, color } = rowInputs(row);
            if (
                row === skipRow
                || typeof id !== 'string'
                || id === ''
                || !(name instanceof HTMLInputElement)
                || !(color instanceof HTMLInputElement)
            ) {
                return;
            }

            const draft = {};
            if (name.value !== name.defaultValue) {
                draft.name = name.value;
            }
            if (color.value !== color.defaultValue) {
                draft.color = color.value;
            }
            if (draft.name !== undefined || draft.color !== undefined) {
                drafts.set(id, draft);
            }
        });

        return drafts;
    };

    const applyDrafts = (drafts) => {
        if (drafts.size === 0) {
            return;
        }

        section.querySelectorAll('[data-status-row]').forEach((row) => {
            const draft = drafts.get(row.dataset.statusId);
            if (draft === undefined) {
                return;
            }

            const { name, color } = rowInputs(row);
            if (draft.name !== undefined && name instanceof HTMLInputElement) {
                name.value = draft.name;
            }
            if (draft.color !== undefined && color instanceof HTMLInputElement) {
                color.value = draft.color;
                // The row is in the document by now, so the accent preview hears this and repaints.
                color.dispatchEvent(new Event('input', { bubbles: true }));
            }
            syncRow(row);
        });
    };

    const findByFocusKey = (key) => {
        if (key === '') {
            return null;
        }

        return Array.from(section.querySelectorAll('[data-status-focus-key]'))
            .find((node) => node.dataset.statusFocusKey === key) ?? null;
    };

    const canTakeFocus = (node) => node instanceof HTMLElement
        && node.disabled !== true
        && node.hidden !== true
        && (typeof node.getClientRects !== 'function' || node.getClientRects().length > 0);

    const findRow = (statusId) => (
        typeof statusId === 'string' && statusId !== ''
            ? Array.from(section.querySelectorAll('[data-status-row]'))
                .find((candidate) => candidate.dataset.statusId === statusId) ?? null
            : null
    );

    /**
     * Focus goes back where the owner was: the control they used (or the one the caller names - the
     * create field, the neighbour of a deleted row), else - when it is now disabled (`Save` on a clean
     * row) or gone (`Use as default` on the new default) - the first control of the same row. The first
     * row's color toggle only covers a key whose row has disappeared meanwhile. None of it scrolls: the
     * swap already kept the content in place, and a focus scroll would undo that.
     */
    const restoreFocus = (key, statusId) => {
        const target = findByFocusKey(key);
        const row = findRow(statusId);
        const rowControl = row instanceof HTMLElement
            ? Array.from(row.querySelectorAll('[data-status-focus-key]')).find(canTakeFocus)
            : null;
        const firstToggle = section.querySelector('[data-status-color-toggle]');
        const next = [target, rowControl, firstToggle].find(canTakeFocus);
        next?.focus({ preventScroll: true });
    };

    /**
     * Emptied first and filled a frame later, so saving the same row twice is still a change the screen
     * reader hears.
     */
    const announce = (message) => {
        if (!(announcer instanceof HTMLElement)) {
            return;
        }

        announcer.textContent = '';
        if (typeof windowRef?.requestAnimationFrame !== 'function') {
            announcer.textContent = message;

            return;
        }
        windowRef.requestAnimationFrame(() => {
            announcer.textContent = message;
        });
    };

    /*
     * A failed write on one row (rename, recolor, default, move) is reported in that row's error slot,
     * where the owner pressed - not in the section feedback at the top, which a phone may have scrolled
     * away. The slot sits inside the row's polite live region. Marked, so a retry or a correction clears
     * only this message, never an empty-name one. A taken name (409) also marks the name field invalid;
     * a colour change cannot correct the name, so it keeps that one. The slot sits above the default and
     * move controls, so a press near the top of the screen would put it under the fixed navigation: it is scrolled into
     * view (`nearest`, against the root's `scroll-padding`, so nothing moves when it is already
     * visible). Focus stays on the pressed control.
     */
    const rowWriteError = (row) => {
        const error = row?.querySelector?.('[data-status-name-error]');

        return error instanceof HTMLElement ? error : null;
    };

    const showRowWriteError = (row, message, duplicateName = false) => {
        const error = rowWriteError(row);
        if (error === null) {
            return false;
        }

        error.dataset.statusWriteError = duplicateName ? 'duplicate' : '';
        if (duplicateName) {
            rowInputs(row).name?.setAttribute('aria-invalid', 'true');
        }
        error.textContent = message;
        error.hidden = false;
        error.scrollIntoView?.({
            block: 'nearest',
            behavior: prefersReducedMotion(windowRef) === true ? 'auto' : 'smooth',
        });

        return true;
    };

    const clearRowWriteError = (row, { keepDuplicate = false } = {}) => {
        const error = rowWriteError(row);
        const kind = error?.dataset.statusWriteError;
        if (kind === undefined || (keepDuplicate && kind === 'duplicate')) {
            return;
        }

        if (kind === 'duplicate') {
            rowInputs(row).name?.removeAttribute('aria-invalid');
        }
        delete error.dataset.statusWriteError;
        error.textContent = '';
        error.hidden = true;
    };

    const showFeedback = (message, tone) => {
        if (typeof dictionaryFeedback?.show === 'function') {
            dictionaryFeedback.show(section, message, tone);

            return;
        }

        const feedback = section.querySelector('[data-status-feedback]');
        const messageNode = feedback?.querySelector('[data-status-feedback-message]');
        if (!(feedback instanceof HTMLElement) || !(messageNode instanceof HTMLElement)) {
            return;
        }

        messageNode.textContent = message;
        feedback.dataset.tone = tone;
        feedback.hidden = false;
    };

    const hideFeedback = async () => {
        const feedback = section.querySelector('[data-status-feedback]');
        if (!(feedback instanceof HTMLElement) || feedback.hidden) {
            return;
        }

        if (typeof dictionaryFeedback?.hide === 'function') {
            await dictionaryFeedback.hide(section);

            return;
        }

        feedback.hidden = true;
    };

    const setBusy = (busy) => {
        pending = busy;
        if (busy) {
            section.setAttribute('aria-busy', 'true');
        } else {
            section.removeAttribute('aria-busy');
        }
    };

    /**
     * @param {string|null} focusKey where focus goes after the swap; null takes the focused control's key
     * @returns {Promise<{ok: boolean, message: string}>} `ok` when the section was replaced by a fresh
     *     server render; otherwise the failure message. A failed delete is reported by its modal alone.
     */
    const runMutation = async (form, submitter, { skipRow = null, beforeReplace = null, focusKey = null } = {}) => {
        const kind = form.dataset.statusForm;
        let failureMessage = kind === 'delete' ? 'Deletion failed. Try again.' : 'Status update failed. Try again.';
        if (pending) {
            return { ok: false, message: failureMessage };
        }

        const drafts = collectDrafts(skipRow);
        const formRow = form.closest?.('[data-status-row]') ?? null;
        const movedStatusId = formRow?.dataset?.statusId;
        const rowWrite = kind === 'edit' || kind === 'default' || kind === 'move';
        const active = root.activeElement;
        const restoreKey = focusKey ?? (
            active instanceof HTMLElement ? String(active.dataset?.statusFocusKey ?? '') : ''
        );
        let duplicateName = false;
        let sentName;
        setBusy(true);
        if (rowWrite) {
            clearRowWriteError(formRow);
            // An earlier `Saved` of this row is over once a new write starts: a failure must not sit beside it.
            const savedMark = formRow?.querySelector?.('[data-status-row-saved]');
            if (savedMark instanceof HTMLElement) {
                savedMark.hidden = true;
            }
        }

        try {
            if (beforeReplace === null) {
                await hideFeedback();
            }
            // The name field stays editable while the request is out (and while the feedback hides): a
            // late 409 marks it only if it still holds the name that was sent, read as the body is built.
            sentName = kind === 'edit' ? rowInputs(formRow ?? form).name?.value : undefined;
            let response;
            try {
                response = await submitDictionaryMutation(
                    form,
                    FRAGMENT_TYPE,
                    windowRef,
                    fetchImpl,
                    submitter,
                );
            } catch (error) {
                failureMessage = UNKNOWN_MESSAGE;
                throw error;
            }
            if (!response.ok) {
                duplicateName = kind === 'edit' && response.status === 409;
                failureMessage = await readErrorMessage(response, failureMessage);
                throw new Error('Status write refused');
            }

            // A success status without a usable fragment: the write may have landed.
            failureMessage = UNKNOWN_MESSAGE;
            const html = await response.text();
            const replacement = parseSection(html);
            if (!(replacement instanceof HTMLElement)) {
                throw new Error('Invalid status response');
            }

            const feedback = replacement.querySelector('[data-status-feedback]');
            const feedbackMessage = feedback?.querySelector('[data-status-feedback-message]');
            let confirmation = null;
            if (
                feedback instanceof HTMLElement
                && feedback.hidden === false
                && feedbackMessage instanceof HTMLElement
            ) {
                confirmation = {
                    message: feedbackMessage.textContent,
                    tone: String(feedback.dataset.tone ?? 'success'),
                };
                feedback.hidden = true;
            }
            await Promise.all([
                collapseBeforeSwap(kind === 'edit' ? skipRow : null),
                typeof beforeReplace === 'function' ? beforeReplace() : null,
            ]);
            // Measured after the collapse, so the reorder and create motion start from what is on screen.
            const previousPositions = kind === 'move' ? collectRowPositions() : null;
            const createSnapshot = kind === 'create' ? collectCreateSnapshot() : null;
            section.replaceWith(replacement);
            section = replacement;
            applyDrafts(drafts);
            await animateCreatedRow(createSnapshot);
            await animateReorderedRows(previousPositions, movedStatusId);
            /*
             * A write on one row (rename, recolor, default, move) is confirmed at that row and announced
             * by name; the server's own message for it only serves the plain-form fallback. Create and
             * delete have no row to point at, so they keep the toast.
             */
            if (rowWrite) {
                flashRowSaved(
                    section.querySelector(`[data-status-id="${movedStatusId}"] [data-status-row-saved]`),
                    windowRef,
                );
                announce(`Saved: ${findRow(movedStatusId)?.dataset.statusName ?? ''}`);
            } else if (confirmation !== null) {
                showFeedback(confirmation.message, confirmation.tone);
            }
            restoreFocus(restoreKey, movedStatusId);
            setBusy(false);

            return { ok: true, message: '' };
        } catch {
            setBusy(false);
            if (rowWrite) {
                const stillSent = duplicateName && rowInputs(formRow ?? form).name?.value === sentName;
                if (!showRowWriteError(formRow, failureMessage, stillSent)) {
                    showFeedback(failureMessage, 'error');
                }
            } else if (kind !== 'delete') {
                showFeedback(failureMessage, 'error');
            }

            return { ok: false, message: failureMessage };
        }
    };

    root.addEventListener('input', (event) => {
        const input = event.target;
        const row = input?.closest?.('[data-status-row]');
        if (
            row instanceof HTMLElement
            && section.contains(row)
            && (input.matches('[data-status-name-input]') || input.matches('[data-status-color-input]'))
        ) {
            syncRow(row);
            clearRowWriteError(row, { keepDuplicate: input.matches('[data-status-color-input]') });
        }
        if (input?.hasAttribute?.('aria-invalid') && section.contains(input)) {
            checkNameField(input, input.form?.querySelector('[data-status-name-error]'));
        }
    });

    root.addEventListener('change', (event) => {
        const swatch = event.target;
        if (!swatch?.matches?.('[data-status-color-swatch]')) {
            return;
        }

        const color = swatch.closest('[data-status-row]')?.querySelector('[data-status-color-input]');
        if (color instanceof HTMLInputElement) {
            color.value = swatch.value;
            color.dispatchEvent(new Event('input', { bubbles: true }));
        }
    });

    root.addEventListener('click', (event) => {
        const target = event.target;
        if (typeof target?.closest !== 'function') {
            return;
        }

        if (target.closest('[data-status-feedback-close]') !== null) {
            const feedback = section.querySelector('[data-status-feedback]');
            if (typeof dictionaryFeedback?.hide === 'function') {
                void dictionaryFeedback.hide(section);
            } else if (feedback instanceof HTMLElement) {
                feedback.hidden = true;
            }

            return;
        }

        // Showing or hiding the palette is all the toggle does: the draft, the row state and the accent
        // preview stay as they are, and focus stays on the toggle.
        const colorToggle = target.closest('[data-status-color-toggle]');
        if (colorToggle instanceof HTMLElement && section.contains(colorToggle)) {
            void setPaletteOpen(
                colorToggle.closest('[data-status-row]'),
                colorToggle.getAttribute('aria-expanded') !== 'true',
            );

            return;
        }

        if (target.closest('[data-status-color-custom]') !== null) {
            const color = target.closest('[data-status-row]')?.querySelector('[data-status-color-input]');
            if (color instanceof HTMLInputElement) {
                color.click();
            }

            return;
        }

        const revertButton = target.closest('[data-status-revert]');
        if (revertButton === null) {
            return;
        }

        const row = revertButton.closest('[data-status-row]');
        if (!(row instanceof HTMLElement)) {
            return;
        }

        const { name, color } = rowInputs(row);
        if (name instanceof HTMLInputElement) {
            name.value = name.defaultValue;
        }
        if (color instanceof HTMLInputElement) {
            color.value = color.defaultValue;
            color.dispatchEvent(new Event('input', { bubbles: true }));
        }
        syncRow(row);
        // The stored name is never wrong: a taken-name or an empty-name mark goes with the draft.
        clearRowWriteError(row);
        if (name?.hasAttribute?.('aria-invalid')) {
            checkNameField(name, rowWriteError(row));
        }
        if (name instanceof HTMLInputElement) {
            name.focus();
        }
    });

    root.addEventListener('submit', (event) => {
        const form = event.target;
        const kind = form?.dataset?.statusForm;
        if (kind === undefined || !section.contains(form)) {
            return;
        }

        event.preventDefault();
        if (
            (kind === 'edit' || kind === 'create')
            && !checkNameField(
                form.querySelector('input[name="name"]'),
                form.querySelector('[data-status-name-error]'),
            )
        ) {
            return;
        }
        void runMutation(form, event.submitter, {
            skipRow: kind === 'edit' ? form.closest('[data-status-row]') : null,
            // The field, not `Add status`, whichever submitted: the next name is typed right away.
            focusKey: kind === 'create' ? 'create-name' : null,
        });
    });

    const deleteModal = initDeleteModal({
        root,
        runMutation,
        getSection: () => section,
        hideFeedback,
        animateDeletedRow,
        windowRef,
    });

    return { closeDeleteModal: () => deleteModal?.close() };
}

/**
 * Confirmation for a destructive action that also has to ask where the games go. The target list is
 * rebuilt from the live rows on every open, so it never offers a status that a previous operation
 * removed or renamed.
 */
function initDeleteModal({ root, runMutation, getSection, hideFeedback, animateDeletedRow, windowRef }) {
    const modal = root.querySelector?.('[data-status-delete-modal]');
    const dialog = modal?.querySelector('[data-status-delete-dialog]');
    const title = modal?.querySelector('[data-status-delete-title]');
    const summary = modal?.querySelector('[data-status-delete-summary]');
    const feedback = modal?.querySelector('[data-status-delete-feedback]');
    const targetBlock = modal?.querySelector('[data-status-delete-target]');
    const select = modal?.querySelector('[data-status-delete-select]');
    const form = modal?.querySelector('[data-status-delete-form]');
    const confirmButton = modal?.querySelector('[data-status-delete-confirm]');
    if (
        !(modal instanceof HTMLElement)
        || !(dialog instanceof HTMLElement)
        || !(title instanceof HTMLElement)
        || !(summary instanceof HTMLElement)
        || !(feedback instanceof HTMLElement)
        || !(targetBlock instanceof HTMLElement)
        || !(select instanceof HTMLElement)
        || !(form instanceof HTMLElement)
        || !(confirmButton instanceof HTMLElement)
    ) {
        return null;
    }

    let deletePending = false;
    let rowToDelete = null;
    // The one owner of `Delete`: usable with an open delete, no request out and, when games have to move,
    // a destination. While pending it says so; focus it held parks on the dialog.
    const syncConfirm = () => {
        const disabled = deletePending
            || form.getAttribute('action') === null
            || (select.hasAttribute('required') && select.value === '');
        if (disabled && root.activeElement === confirmButton) {
            dialog.focus();
        }
        confirmButton.disabled = disabled;
        confirmButton.textContent = deletePending
            ? 'Deleting...'
            : (targetBlock.hidden ? 'Delete' : 'Delete and move');
        if (deletePending) {
            modal.setAttribute('aria-busy', 'true');
        } else {
            modal.removeAttribute('aria-busy');
        }
    };
    const hide = () => {
        modal.hidden = true;
        form.removeAttribute('action');
        feedback.textContent = '';
        feedback.hidden = true;
        syncConfirm();
        focusScope.deactivate();
    };
    const close = () => {
        if (modal.hidden || deletePending) {
            return;
        }

        hide();
    };
    const focusScope = createModalFocusScope({ dialog, requestClose: close, documentRef: root });
    if (focusScope === null) {
        return null;
    }

    root.addEventListener('click', (event) => {
        const target = event.target;
        if (typeof target?.closest !== 'function') {
            return;
        }

        if (target.closest('[data-status-delete-close]') !== null) {
            close();

            return;
        }

        const trigger = target.closest('[data-status-delete]');
        const row = trigger?.closest('[data-status-row]');
        if (!(trigger instanceof HTMLElement) || !(row instanceof HTMLElement)) {
            return;
        }

        const name = String(row.dataset.statusName ?? '');
        const deleteUrl = String(row.dataset.statusDeleteUrl ?? '');
        const gameCount = Number.parseInt(String(row.dataset.statusGameCount ?? '0'), 10) || 0;
        if (name === '' || deleteUrl === '') {
            return;
        }

        form.setAttribute('action', deleteUrl);
        rowToDelete = row;
        feedback.textContent = '';
        feedback.hidden = true;
        title.textContent = `Delete "${name}"?`;

        if (gameCount === 0) {
            summary.textContent = 'This status contains no games.';
            select.replaceChildren();
            select.removeAttribute('required');
            targetBlock.hidden = true;
        } else {
            summary.textContent = `${gameCount} ${gameCount === 1 ? 'game' : 'games'} will move to another status.`;
            // No status is picked for the owner: the games land where they chose, so Delete waits for a choice.
            const placeholder = root.createElement('option');
            placeholder.value = '';
            placeholder.textContent = 'Choose destination status';
            select.replaceChildren(placeholder, ...buildTargetOptions(getSection(), row, root));
            select.value = '';
            select.setAttribute('required', 'required');
            targetBlock.hidden = false;
        }
        syncConfirm();

        modal.hidden = false;
        focusScope.activate(trigger);
        windowRef.requestAnimationFrame(() => focusScope.focus());
    });

    select.addEventListener('change', syncConfirm);

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (
            deletePending
            || form.getAttribute('action') === null
            || (select.hasAttribute('required') && select.value === '')
        ) {
            return;
        }

        deletePending = true;
        feedback.textContent = '';
        feedback.hidden = true;
        syncConfirm();
        const deletingRow = rowToDelete;
        // Focus lands on the row that takes the deleted one's place (else the one above): a button, so a
        // phone opens no keyboard. Read now - the row and its neighbours are gone after the swap.
        const rows = Array.from(getSection().querySelectorAll('[data-status-row]'));
        const index = rows.indexOf(deletingRow);
        const neighbour = index === -1 ? null : (rows[index + 1] ?? rows[index - 1] ?? null);
        const result = await runMutation(form, null, {
            beforeReplace: async () => {
                hide();
                await hideFeedback();
                await animateDeletedRow(deletingRow);
            },
            focusKey: neighbour === null ? '' : `color-${neighbour.dataset.statusId}`,
        });
        deletePending = false;
        syncConfirm();
        if (result.ok) {
            return;
        }

        feedback.textContent = result.message;
        feedback.hidden = false;
        // The destination stays picked; focus returns to `Delete` unless it moved on inside the dialog.
        const active = root.activeElement;
        if (!modal.hidden && (active === dialog || !dialog.contains(active)) && !confirmButton.disabled) {
            confirmButton.focus();
        }
    });

    return { close };
}

/**
 * Saved names, not drafts: the option list has to say where the games really land.
 */
function buildTargetOptions(section, excludedRow, root) {
    return Array.from(section.querySelectorAll('[data-status-row]'))
        .filter((row) => row !== excludedRow)
        .map((row) => {
            const name = row.querySelector('[data-status-name-input]');
            const count = Number.parseInt(String(row.dataset.statusGameCount ?? '0'), 10) || 0;
            const option = root.createElement('option');
            option.value = String(row.dataset.statusId ?? '');
            option.textContent = `${name?.defaultValue ?? ''} · ${count} ${count === 1 ? 'game' : 'games'}`;

            return option;
        });
}
